import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Splash.css';

const CAROUSEL_QUESTIONS = [
    "What makes someone magnetic?",
    "Should I text her?",
    "Why do I always attract the same type?",
    "Do looks matter?",
    "Why does it feel different when you stop trying?",
    "How to get out of the friend zone",
    "What does she actually notice first?",
    "Why do I get ghosted after the second date?",
    "Why do some guys just 'get it'?",
    "What makes a good date?",
    "What am I doing wrong without realizing it?",
    "Why does playing hard to get work?",
];

const STEPS = [
    {
        t: 'You notice a problem',
        d: "Something keeps going wrong in the same place. Maybe you close too early, or the conversation dies at the same beat every time.",
    },
    {
        t: 'Talk about it',
        d: 'Describe what happened, or paste the screenshots. The more honest you are, the more there is to work with.',
    },
    {
        t: 'Get feedback',
        d: 'Specific to that exchange and to your history — not a listicle. One concrete thing to change, with a time estimate.',
    },
    {
        t: 'Go try it for real',
        d: 'It lands in your Actions list only if you accept it. Then you go have the actual interaction.',
    },
    {
        t: 'Repeat',
        d: 'Report back. The result gets logged, the profile sharpens, and the next sticking point comes into focus.',
    },
];

function useOnScreen(ref, threshold = 0.3) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold }
        );
        observer.observe(el);
        // The reveal is decoration; the copy is not. If the observer never
        // delivers (it doesn't fire reliably inside this scroll container),
        // show the content anyway rather than leaving it at opacity 0.
        const fallback = setTimeout(() => setVisible(true), 1800);
        return () => {
            observer.disconnect();
            clearTimeout(fallback);
        };
    }, [ref, threshold]);
    return visible;
}

export default function SplashPage() {
    const navigate = useNavigate();
    const [index, setIndex] = useState(0);
    const [fading, setFading] = useState(false);
    const exampleRef = useRef(null);
    const exampleVisible = useOnScreen(exampleRef, 0.15);

    useEffect(() => {
        const interval = setInterval(() => {
            setFading(true);
            setTimeout(() => {
                setIndex(prev => (prev + 1) % CAROUSEL_QUESTIONS.length);
                setFading(false);
            }, 400);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="splash-page">
            <nav className="splash-nav">
                <button className="splash-nav-link" onClick={() => navigate('/how-it-works')}>
                    How it works
                </button>
                <button className="splash-nav-link" onClick={() => navigate('/auth?mode=signin')}>
                    Sign in
                </button>
            </nav>

            {/* Screen 1: Hero */}
            <section className="splash-hero">
                <div className="splash-content">
                    <h1 className="splash-title">InTension</h1>
                    <div className="splash-subtitle-area">
                        <p className={`splash-question ${fading ? 'fade-out' : 'fade-in'}`}>
                            {CAROUSEL_QUESTIONS[index]}
                        </p>
                    </div>
                </div>
                <div className="splash-scroll-hint">
                    <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
                </div>
            </section>

            {/* Screen 2: What it is */}
            <section className="splash-what">
                <h2 className="splash-what-headline">
                    A social skills coach that actually knows you.
                </h2>

                <div
                    ref={exampleRef}
                    className={`splash-timeline ${exampleVisible ? 'visible' : ''}`}
                >
                    {/* Runs edge to edge and fades out both ends, so the run reads
                        as a slice of something continuous rather than a list. */}
                    <span className="splash-timeline-track" aria-hidden="true" />
                    <ol className="splash-timeline-steps">
                        {STEPS.map((step, i) => (
                            <li
                                key={step.t}
                                className="splash-timeline-step"
                                style={{ transitionDelay: `${i * 0.1}s` }}
                            >
                                <span
                                    className={`splash-timeline-dot${i === STEPS.length - 1 ? ' loop' : ''}`}
                                    aria-hidden="true"
                                >
                                    {i === STEPS.length - 1 && (
                                        <svg viewBox="0 0 24 24">
                                            <path d="M21 12a9 9 0 1 1-3.5-7.1" />
                                            <path d="M21 3v5h-5" />
                                        </svg>
                                    )}
                                </span>
                                <div className="splash-timeline-t">{step.t}</div>
                                <p className="splash-timeline-d">{step.d}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Screen 3: CTA */}
            <section className="splash-cta-section">
                <h2 className="splash-cta-headline">
                    Warning: InTension is only made for people who take action and
                    want immediate results.
                </h2>
                <div className="splash-ctas">
                    <button className="splash-btn primary" onClick={() => navigate('/auth')}>
                        Get started
                    </button>
                    <button className="splash-btn secondary" onClick={() => navigate('/auth?mode=signin')}>
                        Sign in
                    </button>
                </div>
                <button className="splash-how-link" onClick={() => navigate('/how-it-works')}>
                    How it works
                </button>
            </section>
        </div>
    );
}
